import React from 'react';
import PropTypes from 'prop-types';
import { format as formatDate } from 'date-fns';
import { es } from 'date-fns/locale/es';
import useUIStore from '../../store/uiStore';

/**
 * Modal de detalle de cita.
 * Muestra información completa: paciente, tratamiento, fecha/hora, rentabilidad (si completada),
 * cambio rápido de estado y botones de acción (editar, re-agendar, eliminar, cerrar).
 *
 * Props:
 *  - cita: objeto de cita seleccionada
 *  - onClose: callback para cerrar el modal
 *  - onEstadoChange: callback (nuevoEstado) para cambiar estado
 *  - onEdit: callback (cita) para abrir edición
 *  - onDelete: callback (cita) para confirmar eliminación
 */
const CitaDetailModal = ({ cita, onClose, onEstadoChange, onEdit, onDelete }) => {
  const { openQuickBooking } = useUIStore();

  if (!cita) return null;

  const handleReagendar = () => {
    onClose();
    openQuickBooking({
      paciente_nombre: cita.paciente_nombre,
      paciente_telefono: cita.paciente_telefono || cita.telefono,
      tratamiento_id: cita.tratamiento?.id || cita.tratamiento_id,
      tratamiento_nombre: cita.tipo_tratamiento || cita.tratamiento_nombre,
      paciente_rut: cita.paciente_rut,
      paciente_id: cita.paciente_id,
      es_reagendar: true,
    });
  };

  const handleEditClick = () => {
    onEdit(cita);
    onClose();
  };

  const handleDeleteClick = () => {
    onDelete(cita);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-xl overflow-hidden">
        {/* Header con gradiente Teal */}
        <div className="bg-gradient-to-r from-[#2A9D8F] to-[#227f74] px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold text-white">
                Detalle de la Sesión
              </h3>
              <p className="text-white/80 text-sm mt-0.5">
                {formatDate(new Date(`${cita.fecha}T${cita.hora}`), "EEEE, d 'de' MMMM yyyy", { locale: es })}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {/* Badge de Estado */}
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                cita.estado === 'completada'
                  ? 'bg-green-100 text-green-800'
                  : cita.estado === 'cancelada'
                    ? 'bg-red-100 text-red-800'
                    : cita.estado === 'confirmada'
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-amber-100 text-amber-800'
              }`}>
                {cita.estado?.charAt(0).toUpperCase() + cita.estado?.slice(1)}
              </span>
              <button
                className="text-white/80 hover:text-white transition-colors"
                onClick={onClose}
                aria-label="Cerrar detalle"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <div className="p-6">
          {/* Sección de Rentabilidad - Solo para citas completadas */}
          {cita.estado === 'completada' && (
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200 rounded-xl p-5 mb-5">
              <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Rentabilidad de la Sesión
              </h4>
              <div className="grid grid-cols-3 gap-4 text-center">
                {/* Ingreso */}
                <div className="bg-white rounded-lg p-3 shadow-sm">
                  <p className="text-xs text-gray-500 mb-1">Ingreso</p>
                  <p className="text-lg font-bold text-gray-900">
                    ${(cita.precio_tratamiento || cita.tratamiento?.precio || 25000).toLocaleString('es-CL')}
                  </p>
                </div>
                {/* Costo Insumos */}
                <div className="bg-white rounded-lg p-3 shadow-sm">
                  <p className="text-xs text-gray-500 mb-1">Costo Insumos</p>
                  <p className="text-lg font-bold text-[#E76F51]">
                    {cita.costo_real !== null && cita.costo_real !== undefined
                      ? (cita.costo_real > 0
                          ? `-$${Number(cita.costo_real).toLocaleString('es-CL')}`
                          : '$0')
                      : 'Sin receta'}
                  </p>
                </div>
                {/* Margen Real */}
                <div className="bg-white rounded-lg p-3 shadow-sm">
                  <p className="text-xs text-gray-500 mb-1">Margen Real</p>
                  <p className="text-lg font-bold text-[#2A9D8F]">
                    {cita.costo_real != null
                      ? `$${((cita.precio_tratamiento || cita.tratamiento?.precio || 25000) - cita.costo_real).toLocaleString('es-CL')}`
                      : '---'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Información de la Cita */}
          <div className="grid grid-cols-2 gap-6 mb-5">
            {/* Paciente */}
            <div className="col-span-2 sm:col-span-1">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#F4E1D2] rounded-full flex items-center justify-center">
                  <span className="text-[#E76F51] font-bold text-lg">
                    {(cita.paciente_nombre || 'P')[0].toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Paciente</p>
                  <p className="font-semibold text-gray-900">{cita.paciente_nombre || 'No disponible'}</p>
                  <p className="text-sm text-gray-500">{cita.paciente_rut || ''}</p>
                </div>
              </div>
            </div>

            {/* Tratamiento */}
            <div className="col-span-2 sm:col-span-1">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Tratamiento</p>
              <p className="font-semibold text-gray-900">
                {cita.tipo_tratamiento || cita.tratamiento_nombre || 'No especificado'}
              </p>
              <p className="text-sm text-gray-500">
                {(cita.tratamiento?.duracion_minutos || 60) >= 120 ? '2 horas' : `${cita.tratamiento?.duracion_minutos || 60} minutos`}
              </p>
            </div>
          </div>

          {/* Sprint 4: Badge de plan de sesiones */}
          {cita.plan_sesiones_total && (
            <div className="mb-5 p-3 bg-purple-50 border border-purple-200 rounded-lg flex items-center gap-2">
              <span className="text-lg">📋</span>
              <div>
                <p className="text-sm font-semibold text-purple-800">
                  Plan: sesión {cita.plan_sesion_actual} de {cita.plan_sesiones_total}
                </p>
                {cita.plan_descuento > 0 && (
                  <p className="text-xs text-purple-600">
                    {cita.plan_descuento}% de descuento aplicado al paquete
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Fecha y Hora */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Fecha</p>
              <p className="font-semibold text-gray-900">
                {formatDate(new Date(`${cita.fecha}T${cita.hora}`), 'dd/MM/yyyy')}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Hora</p>
              <p className="font-semibold text-gray-900">
                {cita.hora?.substring(0, 5)}
              </p>
            </div>
          </div>

          {/* Cambio Rápido de Estado */}
          {cita.estado !== 'completada' && (
            <div className="mb-5">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Cambiar Estado</p>
              <div className="grid grid-cols-4 gap-2">
                <button
                  onClick={() => onEstadoChange('reservada')}
                  disabled={cita.estado === 'reservada'}
                  className={`py-2 px-2 rounded-lg text-xs font-medium transition-all ${
                    cita.estado === 'reservada'
                      ? 'bg-amber-100 text-amber-800 ring-2 ring-amber-400'
                      : 'bg-gray-100 text-gray-600 hover:bg-amber-50 hover:text-amber-700'
                  }`}
                >
                  📅 Reservada
                </button>
                <button
                  onClick={() => onEstadoChange('confirmada')}
                  disabled={cita.estado === 'confirmada'}
                  className={`py-2 px-2 rounded-lg text-xs font-medium transition-all ${
                    cita.estado === 'confirmada'
                      ? 'bg-blue-100 text-blue-800 ring-2 ring-blue-400'
                      : 'bg-gray-100 text-gray-600 hover:bg-blue-50 hover:text-blue-700'
                  }`}
                >
                  ✅ Confirmada
                </button>
                <button
                  onClick={() => onEstadoChange('completada')}
                  disabled={cita.estado === 'completada'}
                  className={`py-2 px-2 rounded-lg text-xs font-medium transition-all ${
                    cita.estado === 'completada'
                      ? 'bg-green-100 text-green-800 ring-2 ring-green-400'
                      : 'bg-gray-100 text-gray-600 hover:bg-green-50 hover:text-green-700'
                  }`}
                >
                  🎉 Completada
                </button>
                <button
                  onClick={() => onEstadoChange('cancelada')}
                  disabled={cita.estado === 'cancelada'}
                  className={`py-2 px-2 rounded-lg text-xs font-medium transition-all ${
                    cita.estado === 'cancelada'
                      ? 'bg-red-100 text-red-800 ring-2 ring-red-400'
                      : 'bg-gray-100 text-gray-600 hover:bg-red-50 hover:text-red-700'
                  }`}
                >
                  ❌ Cancelada
                </button>
              </div>
            </div>
          )}

          {/* Botones de Acción */}
          <div className="flex gap-3 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium transition-colors"
            >
              Cerrar
            </button>
            <button
              type="button"
              onClick={handleEditClick}
              className="flex-1 px-4 py-2.5 bg-[#2A9D8F] text-white rounded-lg hover:bg-[#227f74] font-medium transition-colors"
            >
              Editar
            </button>
            {/* Botón Re-agendar — abre QuickBooking con datos precargados */}
            {cita?.estado !== 'cancelada' && (
              <button
                type="button"
                onClick={handleReagendar}
                className="px-4 py-2.5 bg-[#2A9D8F] text-white rounded-lg hover:bg-[#227f74] font-medium transition-colors flex items-center gap-1 text-sm"
                title="Re-agendar esta cita"
              >
                🔁 Re-agendar
              </button>
            )}
            <button
              type="button"
              onClick={handleDeleteClick}
              className="px-4 py-2.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 font-medium transition-colors"
              aria-label="Eliminar cita"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

CitaDetailModal.propTypes = {
  cita: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onEstadoChange: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default CitaDetailModal;
